import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useAuthenticationStore } from "@/stores/authentication";
import { UserPermissionsService } from "@/services/userPermissions";
import type { CurrentUserResponse, Community, Role } from "@/types/api";

interface UserPermissions {
    hasOperatorOrAdminRole: boolean;
    primaryCommunity: Community | null;
    appUrl: string | null;
}

/**
 * Composable for user data and permissions
 * Centralizes all user-related business logic and permissions
 */
export function useMe() {
    const authStore = useAuthenticationStore();
    const { user } = storeToRefs(authStore);

    // Role-based computed properties
    const roles = computed((): Role[] => {
        return user.value ? UserPermissionsService.getRoles(user.value) : [];
    });

    const hasRole = (role: Role): boolean => roles.value.includes(role);

    const hasOperatorOrAdminRole = computed((): boolean => {
        return user.value
            ? UserPermissionsService.hasOperatorOrAdminRole(user.value)
            : false;
    });

    const primaryCommunity = computed((): Community | null => {
        return user.value
            ? UserPermissionsService.getPrimaryCommunity(user.value)
            : null;
    });

    const appUrl = computed((): string | null => {
        return user.value
            ? UserPermissionsService.buildAppUrlForUser(user.value)
            : null;
    });

    // Comprehensive permissions object
    const permissions = computed((): UserPermissions | null => {
        if (!user.value) return null;

        return {
            hasOperatorOrAdminRole: hasOperatorOrAdminRole.value,
            primaryCommunity: primaryCommunity.value,
            appUrl: appUrl.value,
        };
    });

    return {
        // User data
        user,

        // Role checking
        roles,
        hasRole,
        hasOperatorOrAdminRole,

        // Community & navigation
        primaryCommunity,
        appUrl,

        // Comprehensive permissions
        permissions,

        // Auth store methods
        fetchCurrentUser: authStore.fetchCurrentUser,
        isAuthenticated: authStore.isAuthenticated,
    };
}

export type { UserPermissions };
